import { useReducer, useEffect, useCallback } from 'react';
import axios, { CancelToken } from 'axios';
import { REGIONS } from '@adsk/bim360-matrix-react-helpers/es/consts';
import { useEnv } from '../contexts/env';
import { useToken } from '../contexts/token';

const FETCH_ACTIONS = {
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE'
};

const CONFIG_ACTIONS = {
  SET_CONFIG: 'SET_CONFIG',
  MANUAL_SET_CONFIG: 'MANUAL_SET_CONFIG',
  REFRESH: 'REFRESH'
};

const dataFetchReducer = (state, action) => {
  switch (action.type) {
    case FETCH_ACTIONS.FETCH_INIT:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case FETCH_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
        data: action.payload
      };
    case FETCH_ACTIONS.FETCH_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    default:
      throw new Error();
  }
};

const configReducer = (state, action) => {
  switch (action.type) {
    case CONFIG_ACTIONS.SET_CONFIG:
      return {
        ...state,
        prevConfig: action.payload,
        config: action.payload
      };
    case CONFIG_ACTIONS.MANUAL_SET_CONFIG:
      return {
        ...state,
        config: action.payload,
        requestId: state.requestId + 1
      };
    case CONFIG_ACTIONS.REFRESH:
      return {
        ...state,
        requestId: state.requestId + 1
      };
    default:
      throw new Error();
  }
};

/**
 * useDataApi hook can be used to fetch data and get the request status easily. This hook adds `Authorization` header based on `TokenContext`.
 * A new request would be sent each time:
 *  - `axiosConfig` is changed
 *  - calling `refresh`
 *  - calling `doFetch`
 *
 * If you need request to initiate only upon manual interaction, pass an empty `axiosConfig` and call `doFetch` with the needed `axiosConfig`.
 *
 * @param axiosConfig - axios config. Can be a string if you need `get` request.
 * @returns {{isLoading: boolean, data: object, refresh: function, error: object, doFetch: function}}
 */
const useDataApi = axiosConfig => {
  const { region } = useEnv();
  const { onTokenExpired, getToken } = useToken();

  const [fetchState, dispatchFetchAction] = useReducer(dataFetchReducer, {
    isLoading: false,
    error: null,
    data: null
  });
  const [configState, dispatchConfigAction] = useReducer(configReducer, {
    config: axiosConfig,
    prevConfig: axiosConfig,
    requestId: 0
  });

  if (axiosConfig !== configState.prevConfig) {
    // Check if hook's `axiosConfig` was changed. In that case we need to initiate a new request.
    dispatchConfigAction({ type: CONFIG_ACTIONS.SET_CONFIG, payload: axiosConfig });
  }

  useEffect(() => {
    if (!configState.config) return;

    dispatchFetchAction({ type: FETCH_ACTIONS.FETCH_INIT });

    const source = CancelToken.source();

    const axiosRequestConfig = {
      cancelToken: source.token
    };
    if (typeof configState.config === 'string') {
      axiosRequestConfig.url = configState.config;
      axiosRequestConfig.method = 'GET';
    } else {
      Object.assign(axiosRequestConfig, configState.config);
    }

    getToken()
      .then(token =>
        axios.request({
          ...axiosRequestConfig,
          headers: {
            Authorization: `Bearer ${token}`,
            ...(region === REGIONS.FEDRAMP && {
              region
            }),
            ...axiosRequestConfig.headers
          }
        })
      )
      .then(response => {
        dispatchFetchAction({ type: FETCH_ACTIONS.FETCH_SUCCESS, payload: response.data });
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          if (error.response && error.response.status === 401) {
            onTokenExpired();
          }
          dispatchFetchAction({ type: FETCH_ACTIONS.FETCH_FAILURE, payload: error });
        }
      });

    // eslint-disable-next-line consistent-return
    return source.cancel;
    // We don't want to trigger another call when `getToken` or `onTokenExpired` are changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configState.config, configState.requestId]);

  const refresh = useCallback(() => dispatchConfigAction({ type: CONFIG_ACTIONS.REFRESH }), []);

  const doFetch = useCallback(
    axiosConfig =>
      dispatchConfigAction({ type: CONFIG_ACTIONS.MANUAL_SET_CONFIG, payload: axiosConfig }),
    []
  );

  return {
    ...fetchState,
    refresh,
    doFetch
  };
};

export default useDataApi;
